"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle
} from "@/components/ui/card";
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious
} from "@/components/ui/carousel";
import type { CarouselApi } from "@/components/ui/carousel";
import type { MarketingContentItem } from "@/features/marketing/content/marketing-content";
import { cn } from "@/lib/utils";

interface MarketingContentCollectionProps {
	title: string;
	description?: string;
	eyebrow?: string;
	items: MarketingContentItem[];
	display?: "rail" | "grid";
	cardSize?: "compact" | "large";
	viewAllHref?: string;
	viewAllLabel?: string;
	className?: string;
	limit?: number;
}

function format_date(date: string) {
	const parsed = new Date(date);
	if (Number.isNaN(parsed.getTime())) return null;

	return new Intl.DateTimeFormat("en-GB", {
		day: "numeric",
		month: "long",
		year: "numeric"
	}).format(parsed);
}

export function ContentCard({
	item,
	cardSize = "large"
}: {
	item: MarketingContentItem;
	cardSize?: "compact" | "large";
}) {
	const is_compact = cardSize === "compact";
	const formatted_date = format_date(item.date);
	const action_label = item.audience === "news" ? "Read update" : "Read story";

	return (
		<Link href={item.href} className="block h-full" aria-label={`Read ${item.title}`}>
			<Card
				className={cn(
					"marketing-content-card group flex h-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.035)] transition duration-300 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_18px_40px_rgba(15,23,42,0.07)]",
					is_compact ? "min-h-[21.5rem]" : "min-h-[28rem]"
				)}
			>
				<div className={cn("shrink-0", is_compact ? "p-2 pb-0" : "p-3 pb-0")}>
					<div
						className={cn(
							"relative w-full overflow-hidden rounded-md bg-slate-100",
							is_compact ? "aspect-[16/9]" : "aspect-[16/10]"
						)}
					>
						<Image
							src={item.image}
							alt={item.imageAlt}
							fill
							sizes={is_compact ? "(max-width: 768px) 84vw, 25vw" : "(max-width: 768px) 100vw, 33vw"}
							className="object-cover transition duration-500 group-hover:scale-[1.03]"
						/>
						{item.logo && (
							<div
								className={cn(
									"absolute rounded-md border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.12)]",
									is_compact ? "left-2 top-2" : "left-3 top-3"
								)}
							>
								<div
									className={cn(
										"relative overflow-hidden rounded-md bg-white",
										is_compact ? "h-8 w-11" : "h-10 w-14"
									)}
								>
									<Image
										src={item.logo}
										alt={item.logoAlt || `${item.title} logo`}
										fill
										sizes={is_compact ? "48px" : "56px"}
										className="object-contain p-1"
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			<CardHeader className={cn("gap-2", is_compact ? "p-4 pb-2" : "p-5 pb-3 sm:p-6 sm:pb-3")}>
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
					<span className="font-semibold text-ss-blue">{item.label}</span>
					{formatted_date && <span className="text-slate-500">{formatted_date}</span>}
				</div>
				<CardTitle
					className={cn(
						"!font-serif !font-semibold !leading-tight !tracking-normal text-slate-950 transition group-hover:text-ss-blue",
						is_compact ? "line-clamp-3 !text-[1.05rem] sm:!text-lg" : "line-clamp-3 !text-2xl sm:!text-[1.55rem]"
					)}
				>
					{item.title}
				</CardTitle>
			</CardHeader>
			<CardContent className={cn("flex flex-1 flex-col", is_compact ? "px-4 pb-0" : "px-5 pb-0 sm:px-6")}>
				<CardDescription
					className={cn(
						"flex-1 !font-sans !leading-relaxed text-slate-600",
						is_compact ? "line-clamp-2 !text-[13px]" : "line-clamp-3 !text-base"
					)}
				>
					{item.description}
				</CardDescription>
			</CardContent>
			<CardFooter className={cn(is_compact ? "p-4 pt-4" : "p-5 pt-5 sm:p-6 sm:pt-5")}>
				<span className="inline-flex w-full items-center justify-between border-t border-slate-100 pt-3 text-sm font-semibold text-slate-950">
					<span>{action_label}</span>
					<ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" aria-hidden="true" />
				</span>
			</CardFooter>
		</Card>
		</Link>
	);
}

function ContentCarousel({
	items,
	cardSize
}: {
	items: MarketingContentItem[];
	cardSize: "compact" | "large";
}) {
	const [api, set_api] = useState<CarouselApi>();
	const [current, set_current] = useState(0);
	const [count, set_count] = useState(0);

	useEffect(() => {
		if (!api) return;

		const sync_state = () => {
			set_count(api.scrollSnapList().length);
			set_current(api.selectedScrollSnap());
		};

		sync_state();
		api.on("select", sync_state);
		api.on("reInit", sync_state);

		return () => {
			api.off("select", sync_state);
			api.off("reInit", sync_state);
		};
	}, [api]);

	return (
		<Carousel
			setApi={set_api}
			opts={{ align: "start", containScroll: "trimSnaps", dragFree: false }}
			className="relative overflow-hidden"
			aria-label="Featured marketing stories"
		>
			<CarouselContent className="-ml-4 py-1 pr-10 sm:pr-16 lg:pr-24">
				{items.map((item) => (
					<CarouselItem
						key={item.id}
						className="basis-[82%] pl-4 sm:basis-[46%] lg:basis-[31%] xl:basis-[30%]"
					>
						<ContentCard item={item} cardSize={cardSize} />
					</CarouselItem>
				))}
			</CarouselContent>
			<div className="mt-5 flex items-center justify-between gap-4">
				<div className="flex gap-2" aria-label="Choose slide">
					{Array.from({ length: count }).map((_, index) => (
						<button
							key={index}
							type="button"
							aria-label={`Go to slide ${index + 1}`}
							aria-current={index === current ? "true" : undefined}
							onClick={() => api?.scrollTo(index)}
							className={cn(
								"h-2 rounded-full transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ss-blue",
								index === current ? "w-8 bg-ss-blue" : "w-2.5 bg-slate-300 hover:bg-slate-400"
							)}
						/>
					))}
				</div>
				<div className="flex gap-2">
					<CarouselPrevious className="static size-9 translate-y-0 border-slate-200 bg-white shadow-sm disabled:cursor-not-allowed disabled:opacity-40" />
					<CarouselNext className="static size-9 translate-y-0 border-slate-200 bg-white shadow-sm disabled:cursor-not-allowed disabled:opacity-40" />
				</div>
			</div>
		</Carousel>
	);
}

export function MarketingContentCollection({
	title,
	description,
	eyebrow,
	items,
	display = "grid",
	cardSize = "large",
	viewAllHref,
	viewAllLabel = "View all",
	className,
	limit
}: MarketingContentCollectionProps) {
	const visible_items = typeof limit === "number" ? items.slice(0, limit) : items;
	const is_rail = display === "rail";

	return (
		<section className={cn("w-full py-12 sm:py-16", className)}>
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="mb-7 flex flex-col gap-4 sm:mb-9 md:flex-row md:items-end md:justify-between">
					<div className="max-w-3xl">
						{eyebrow && (
							<p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-ss-blue">
								{eyebrow}
							</p>
						)}
						<h2 className="text-2xl font-semibold leading-tight text-slate-950 sm:text-3xl">
							{title}
						</h2>
						{description && (
							<p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
								{description}
							</p>
						)}
					</div>
					{viewAllHref && (
						<Link href={viewAllHref} className="w-fit">
							<Button variant="outline">
								{viewAllLabel}
							</Button>
						</Link>
					)}
				</div>

				{is_rail ? (
					<ContentCarousel items={visible_items} cardSize={cardSize} />
				) : (
					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
						{visible_items.map((item) => (
							<ContentCard key={item.id} item={item} cardSize={cardSize} />
						))}
					</div>
				)}
			</div>
		</section>
	);
}
